import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin } from "lucide-react";
import { PageHeader } from "../../components/common/UIComponents";

export const AdminSettings: React.FC = () => {
  const navigate = useNavigate();

  const [gstRate, setGstRate] = useState('18');
  const [commission, setCommission] = useState('10');
  const [printRate, setPrintRate] = useState('6000');
  const [installRate, setInstallRate] = useState('4500');
  const [removalRate, setRemovalRate] = useState('2500');
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(true);
  const [whatsapp, setWhatsapp] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2500); };

  // ── Shared primitives ──────────────────────────────────────────────────────

  const SectionBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white border border-[var(--border)] rounded-2xl p-5 shadow-sm">
      <h3 className="font-bold text-[15px] text-[var(--text-primary)] m-0 mb-4 tracking-tight">{title}</h3>
      {children}
    </div>
  );

  const Field: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    prefix?: string;
  }> = ({ label, value, onChange, prefix }) => (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-medium text-[var(--text-secondary)] font-['DM_Sans']">{label}</span>
      <div className="flex items-center border border-[var(--border)] rounded-[10px] bg-white overflow-hidden focus-within:border-[var(--brand)] focus-within:ring-1 focus-within:ring-[var(--brand)] transition-colors">
        {prefix && (
          <span className="pl-3 pr-1 text-[13px] text-[var(--text-muted)] font-['DM_Sans']">{prefix}</span>
        )}
        <input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2.5 text-[13px] font-['DM_Sans'] text-[var(--text-primary)] outline-none bg-transparent"
        />
      </div>
    </label>
  );

  const ToggleRow: React.FC<{
    label: string;
    sublabel: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }> = ({ label, sublabel, checked, onChange }) => (
    <div className="flex justify-between items-center py-3 border-b border-[var(--border-light)] last:border-b-0">
      <div>
        <div className="font-medium text-[13px] text-[var(--text-primary)] font-['DM_Sans']">{label}</div>
        <div className="text-[11px] text-[var(--text-muted)] mt-0.5 font-['DM_Sans']">{sublabel}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ${
          checked ? 'bg-[var(--brand)]' : 'bg-[var(--border)]'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="bg-[var(--bg-base)] min-h-screen px-8 py-7 page-enter">
      <PageHeader title="Platform Settings" subtitle="Configure taxes, commissions, charges and permissions" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">

        {/* Tax & Commission */}
        {/* <SectionBox title="Tax & Commission">
          <div className="flex flex-col gap-3.5">
            <Field label="GST Rate (%)" value={gstRate} onChange={setGstRate} />
            <Field label="Default Platform Commission (%)" value={commission} onChange={setCommission} />
            <div className="bg-[var(--warning-bg)] border border-[#fde8a8] rounded-[10px] px-3.5 py-2.5">
              <p className="text-[var(--warning)] font-['DM_Sans'] text-xs m-0 font-medium">
                ⚠️ Commission is hidden from providers. Visible only to the Admin team.
              </p>
            </div>
          </div>
        </SectionBox> */}

        {/* Service Charges */}
        {/* <SectionBox title="Service Charge Templates">
          <div className="flex flex-col gap-3.5">
            <Field label="Default Printing Charge (₹)" value={printRate} onChange={setPrintRate} prefix="₹" />
            <Field label="Default Installation Charge (₹)" value={installRate} onChange={setInstallRate} prefix="₹" />
            <Field label="Removal / Dismantling Charge (₹)" value={removalRate} onChange={setRemovalRate} prefix="₹" />
          </div>
        </SectionBox> */}

        {/* Notification Channels */}
        <SectionBox title="Notification Channels">
          <div className="flex flex-col">
            <ToggleRow label="Email Notifications" sublabel="Order updates, approvals, payments" checked={emailNotif} onChange={setEmailNotif} />
            <ToggleRow label="SMS Notifications" sublabel="Booking confirmations and reminders" checked={smsNotif} onChange={setSmsNotif} />
            <ToggleRow label="WhatsApp Notifications" sublabel="Rich messages with campaign details" checked={whatsapp} onChange={setWhatsapp} />
          </div>
        </SectionBox>

        {/* RBAC */}
        <SectionBox title="Role-Based Access Control">
          <div className="flex flex-col gap-2">
            {[
              { role: 'Super Admin', perms: ['All Access'], color: 'var(--brand)' },
              { role: 'Operations Manager', perms: ['Listings', 'Bookings', 'Reports'], color: '#3b82f6' },
              { role: 'Field Coordinator', perms: ['Bookings', 'Staff Assignment'], color: '#8b5cf6' },
              { role: 'Finance Manager', perms: ['Payments', 'Reports', 'Invoices'], color: '#22c55e' },
            ].map(r => (
              <div
                key={r.role}
                className="bg-[var(--bg-muted)] rounded-[11px] px-3.5 py-2.5 flex justify-between items-center flex-wrap gap-2 border border-[var(--border-light)]"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                  <span className="font-semibold text-[13px] text-[var(--text-primary)] font-['DM_Sans']">{r.role}</span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {r.perms.map(p => (
                    <span
                      key={p}
                      className="bg-white border border-[var(--border)] text-[var(--text-secondary)] px-2 py-0.5 rounded text-[11px] font-['DM_Sans']"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SectionBox>

        {/* Security */}
        <SectionBox title="Security">
          <div className="flex flex-col gap-2">
            {[
              { label: 'Audit Logs', sublabel: 'Track all admin actions', active: true },
              { label: 'Secure File Uploads', sublabel: 'Scan uploads for malware', active: true },
              { label: 'Data Encryption', sublabel: 'Encrypt sensitive data at rest', active: true },
              { label: 'Two-Factor Auth', sublabel: 'Require 2FA for admin logins', active: false },
            ].map(item => (
              <div
                key={item.label}
                className="flex justify-between items-center px-3.5 py-2.5 bg-[var(--bg-muted)] rounded-[10px] border border-[var(--border-light)]"
              >
                <div>
                  <div className="font-medium text-[13px] text-[var(--text-primary)] font-['DM_Sans']">{item.label}</div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5 font-['DM_Sans']">{item.sublabel}</div>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold font-['DM_Sans'] border ${
                    item.active
                      ? 'bg-[var(--success-bg)] text-[var(--success)] border-[#b8e8ce]'
                      : 'bg-[var(--bg-muted)] text-[var(--text-muted)] border-[var(--border)]'
                  }`}
                >
                  {item.active ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ))}
          </div>
        </SectionBox>

        {/* Location Type Master */}
        <SectionBox title="Location Master">
          <p className="font-['DM_Sans'] text-xs text-[var(--text-muted)] m-0 mb-3.5">
            Manage location types, traffic levels and commission rates used across listings.
          </p>
          <button
            onClick={() => navigate('/admin/settings/location-type-master')}
            className="btn-brand flex items-center gap-2 px-12 py-2.5 text-[13px]"
          >
            <MapPin size={16} /> Location Type Master
          </button>
        </SectionBox>

        {/* Save */}
        {/* <div className="md:col-span-2 flex items-center gap-3.5">
          <button onClick={handleSave} className="btn-brand px-7 py-3 text-sm">
            {saved ? '✓ Settings Saved!' : 'Save All Settings'}
          </button>
          {saved && (
            <span className="text-[var(--success)] text-[13px] font-medium font-['DM_Sans']">
              Changes saved successfully.
            </span>
          )}
        </div> */}
      </div>
    </div>
  );
};